/**
 * 4.5 — o som de interface (UI-22, D0, D15).
 *
 * ## Por que isto não é o `SoundController`
 *
 * O `SoundController` é um módulo Vuex, e **um primitivo não importa store** (D4). Se o
 * `FishButton` fizesse `this.$store.dispatch('SoundController/playSound')`, ele deixaria de ser um
 * primitivo: passaria a exigir que toda aplicação que o use tenha aquele módulo registrado com
 * aquele nome — que é exatamente o acoplamento que fez cada tela reescrever o próprio botão.
 *
 * Este arquivo é uma **folha**: não importa nada. O `App.vue` sincroniza o volume e o mudo do store
 * para cá uma vez, e os primitivos só chamam `tocar()`.
 *
 * ## Por que o som toca aqui e não na tela
 *
 * O UI-22 é *"210 `@click` e nenhum toca som"*. Pedir que cada tela lembre de tocar o clique é
 * garantir que 210 lugares esqueçam — foi assim que se chegou ao estado atual. O clique mora **no
 * `FishButton`**, então ele existe para todo botão do jogo sem ninguém precisar lembrar.
 *
 * ## O detalhe que faz o som não atrapalhar
 *
 * **Cada som tem um teto de repetição.** Sem isso, uma lista de vinte encaixes tocada em sequência
 * (arrastar sobre eles, rolar com o dedo) dispara vinte cliques sobrepostos e vira estática. O
 * `ULTIMO_TOQUE` engole disparos do mesmo som dentro da janela.
 *
 * ## E o autoplay
 *
 * Navegador não deixa tocar áudio antes de um gesto do usuário. O `App.vue` já resolve isso para a
 * música (ouvintes de `click`/`touchstart` que inicializam na primeira interação); aqui a garantia é
 * mais simples: **o primeiro som só pode vir de um clique**, que já é um gesto. O `catch` mudo cobre
 * o resto — som de UI que falha nunca deve virar erro na tela.
 */

/** Onde os arquivos moram. Gerados por `tools/assets/gerar-sons-ui.mjs`. */
const SONS = {
  clique: '/sounds/ui/ui-clique.wav',
  compra: '/sounds/ui/ui-compra.wav',
  erro: '/sounds/ui/ui-erro.wav',
  recompensa: '/sounds/ui/ui-recompensa.wav',
};

/**
 * A janela mínima entre dois disparos do mesmo som.
 *
 * 70ms para o clique é abaixo do que se percebe como "dois cliques" e acima do que a rolagem
 * consegue disparar. Os outros são mais longos porque são mais longos.
 */
const INTERVALO = { clique: 70, compra: 250, erro: 250, recompensa: 400 };

const ultimoToque = {};
const cache = {};

let volume = 0.7;
let mudo = false;

/**
 * Sincroniza com as preferências do jogador. Chamado pelo `App.vue`, uma vez e a cada mudança —
 * é a única ponte entre o store e este módulo, e ela vai numa direção só.
 */
export function configurarSom({ volume: v, mudo: m }) {
  if (typeof v === 'number') volume = Math.max(0, Math.min(1, v));
  if (typeof m === 'boolean') mudo = m;
}

/**
 * Toca um som de interface.
 *
 * O elemento é **clonado do cache** a cada toque: reusar o mesmo `Audio` faria o segundo clique
 * cortar o primeiro pela metade, e dois cliques rápidos são normais. O clone herda o buffer já
 * baixado, então não há requisição nova.
 */
export function tocar(nome) {
  if (mudo || volume <= 0) return;

  const caminho = SONS[nome];
  if (!caminho) return;

  const agora = Date.now();
  if (agora - (ultimoToque[nome] || 0) < (INTERVALO[nome] || 100)) return;
  ultimoToque[nome] = agora;

  try {
    if (!cache[nome]) {
      cache[nome] = new Audio(caminho);
      cache[nome].preload = 'auto';
    }

    const eco = cache[nome].cloneNode();
    eco.volume = volume;
    // `.play()` devolve uma Promise que rejeita quando o navegador barra o autoplay. Sem o `catch`
    // isso vira "Unhandled Promise Rejection" no console a cada som barrado, o que polui o log de
    // erros de verdade — e o `errorInterceptor` deste projeto reporta tudo.
    eco.play().catch(() => {});
  } catch {
    /* som é enfeite: nunca deve derrubar uma interação */
  }
}

/**
 * Pré-carrega os quatro. Chamado na primeira interação, junto da inicialização do áudio.
 *
 * Sem isto, o **primeiro** clique do jogo toca depois de uma ida à rede — e um clique que soa
 * 200ms atrasado é pior que clique nenhum, porque parece travamento em vez de silêncio.
 */
export function prepararSons() {
  for (const [nome, caminho] of Object.entries(SONS)) {
    if (cache[nome]) continue;
    try {
      cache[nome] = new Audio(caminho);
      cache[nome].preload = 'auto';
      cache[nome].load();
    } catch {
      /* idem */
    }
  }
}

export default { tocar, configurarSom, prepararSons };
